import React from 'react'
import { formatEnumItem, isObject, navigateToReportPage } from '../../helpers'
import {
  deprecatedScanOptions,
  DeprecatedScanOptions,
  ScanProfileOptions,
} from 'app/modules/user/features/profile/types'
import { useNavigate } from 'react-router'
import { useReport } from '../../hooks'
import { ReportFilterStat } from '../../components/filter/types'
import { CategoryIcon } from '../../components/category-icon'
import { SignalVerdict, IOCGroupedByFileType, FileProgress, ScanState, TaskProgress } from '../../types'
import {
  EmulationCounts,
  EmulationDataToFlatten,
  FlattenTree,
  ScanStageType,
  TopCategory,
  ThreatReputationStepInfo,
  VerdictCount,
} from './types'
import { NodeType, RawNodeDatumWithVerdict, TreeNode } from '../../types/tree'
import { ButtonAll } from 'app/components/button-all'
import { useExport } from '../../contexts'
import { buildRawNodeDatum } from '../emulation-data/emulation-graph/helper'
import { threatVerdicts } from 'app/modules/reports/types'
import { getThreatVerdicts, getOrderedThreatVerdicts } from '../../helpers/signal-type'

export function formatScanOptions(options: (ScanProfileOptions & DeprecatedScanOptions) | undefined) {
  if (!options) {
    return undefined
  }

  const items: string[] = []
  Object.keys(options).forEach((item) => {
    const key = item as keyof ScanProfileOptions
    const showOption = options[key] && item !== 'scan_mode' && !deprecatedScanOptions.includes(key)
    if (showOption) {
      items.push(formatEnumItem(item))
    }
  })

  return items.join(', ')
}

// Show human readable description of how long ago was given date
export function agoDate(start: Date): [number, string] | string {
  const second = 1000
  const minute = 60 * second
  const hour = 60 * minute
  const day = 24 * hour
  const week = 7 * day
  const month = 30 * day
  const year = 12 * month

  const roundValue = (value: number) => {
    const leftover = value > 1 ? value % 1 : value

    return leftover <= 0.75 ? Math.floor(value) : Math.ceil(value)
  }

  const format = (result: [number, string]) => {
    if (result[0] > 1) {
      result[1] += 's'
    }

    result[1] += ' ago'

    return result
  }

  const startDate = new Date(start)
  const endDate = new Date()
  const diff = endDate.getTime() - startDate.getTime()

  const years = roundValue(diff / year)
  if (years) {
    return format([years, 'year'])
  }

  const months = roundValue(diff / month)
  if (months) {
    return format([months, 'month'])
  }

  const weeks = roundValue(diff / week)
  if (weeks) {
    return format([weeks, 'week'])
  }

  const days = roundValue(diff / day)
  if (days) {
    return format([days, 'day'])
  }

  const hours = roundValue(diff / hour)
  if (hours) {
    return format([hours, 'hour'])
  }

  const minutes = roundValue(diff / minute)
  if (minutes) {
    return format([minutes, 'minute'])
  }

  const seconds = roundValue(diff / second)
  if (seconds) {
    return format([seconds, 'second'])
  }

  return 'now'
}

// Format total scan time as minutes and seconds
export function formatTotalTime(value: number | undefined) {
  if (typeof value === 'undefined') {
    return undefined
  }

  const result = []
  const minutes = Math.floor(value / 60)
  if (minutes) {
    result.push(minutes + 'm')
  }

  result.push((value % 60) + 's')
  return result.join(' ')
}

export function ReportButtonAll({
  count,
  text,
  parentClass = 'summary-card',
  subpage,
}: {
  count?: number
  text: string
  parentClass?: string
  subpage: string
}) {
  const { exporting } = useExport()
  const navigate = useNavigate()
  const { flowId, reportId } = useReport()

  if (exporting) {
    return null
  }

  return (
    <ButtonAll
      id={`overview-all-${subpage}`}
      count={count}
      text={text}
      parentClass={parentClass}
      onClick={() =>
        navigateToReportPage(navigate, flowId as string, reportId as string, subpage, { clearFilter: '1' })
      }
    />
  )
}

// Pick top verdicts from page data, that has filter data
export function pickTopCategories(
  stat: ReportFilterStat | undefined,
  hasMalicious: boolean,
  orderedVerdicts: SignalVerdict[]
) {
  if (!stat) {
    return []
  }

  const result: TopCategory[] = []
  const limit = hasMalicious ? 3 : 2
  const otherVerdicts: SignalVerdict[] = []
  let countOther = 0

  for (const verdict of orderedVerdicts) {
    const count = stat.types?.verdict?.[verdict]
    if (typeof count === 'undefined') {
      continue
    }

    if (result.length < limit) {
      result.push({ variant: verdict, count, verdicts: [verdict] })
    } else {
      otherVerdicts.push(verdict)
      countOther += count
    }
  }

  if (countOther > 0) {
    result.push({ variant: 'neutral', count: countOther, text: 'other', verdicts: otherVerdicts })
  }

  return result
}

export function TopCategories({
  data,
  showText = true,
  onClick,
}: {
  data: TopCategory[]
  showText?: boolean
  onClick?: (verdicts: SignalVerdict[]) => void
}): JSX.Element | null {
  return data.length > 1 ? (
    <div className="d-flex w-100 gap-x-2 mb-4">
      {data.map((item, idx) => (
        <CategoryIcon
          key={item.variant + idx}
          variant={item.variant}
          amount={item.count}
          text={item.text}
          className="col"
          onClick={() => onClick?.(item.verdicts)}
          showText={showText}
        />
      ))}
    </div>
  ) : null
}

export function nodeWithActionAttribute(node: TreeNode): RawNodeDatumWithVerdict {
  const action = node.action
  const type = node.type === NodeType.other ? action : node.type
  let target = node.extra.CommandLine

  switch (type) {
    case 'process':
      target = node.extra.Program
      break
    case 'file':
      target = node.extra.File
      break
    case 'network':
      target = node.extra.URI
      break
    case 'script':
      target = node.extra.Alias
      break
  }

  const nodeInfo: RawNodeDatumWithVerdict = {
    name: type,
    attributes: { target, action, nodeId: node.extra.nodeId },
  }

  if (node.verdictInfo) {
    nodeInfo.verdictInfo = node.verdictInfo
  }

  return nodeInfo
}

function searchTreeAndCount(treeRoot: RawNodeDatumWithVerdict) {
  const queue = [treeRoot]
  const counts: EmulationCounts = { process: 0, network: 0, other: 0 }
  const countsThreat: EmulationCounts = { process: 0, network: 0, other: 0 }
  let total = 0

  for (const node of queue) {
    total++

    const nodeType = node.name === 'network' || node.name === 'process' ? node.name : 'other'
    const isThreat = threatVerdicts.includes(node.verdictInfo?.verdict as SignalVerdict)

    counts[nodeType]++

    if (isThreat) {
      countsThreat[nodeType]++
    }

    if (node.children) {
      queue.push(...node.children)
    }
  }

  return { counts, countsThreat, total }
}

export function detectThreatIndicatorSection(id: string): ScanStageType {
  if (id.match(/^I\d+/)) {
    return ScanStageType.Reputation_service
  }

  if (id.match(/^(PE)?EMU\d+/)) {
    return ScanStageType.Dynamic_fast_pass
  }

  return ScanStageType.Deep_static_analysis
}

export function flattenTrees(emulationData?: EmulationDataToFlatten) {
  if (!emulationData) {
    return null
  }

  const trees: FlattenTree[] = []

  if (emulationData.input?.tree) {
    const tree = buildRawNodeDatum(emulationData.input.tree, nodeWithActionAttribute)
    trees.push({ tree, title: emulationData.input.title, id: emulationData.input.title, type: 'input' })
  }

  if (emulationData.extracted) {
    Object.entries(emulationData.extracted).forEach(([id, val], idx) => {
      if (isObject(val) && val.tree) {
        const tree = buildRawNodeDatum(val.tree, nodeWithActionAttribute)
        trees.push({ tree, title: `Extracted File ${idx + 1}`, id, type: 'extracted' })
      }
    })
  }

  if (emulationData.downloaded) {
    Object.entries(emulationData.downloaded).forEach(([id, val], idx) => {
      if (isObject(val) && val.tree) {
        const tree = buildRawNodeDatum(val.tree, nodeWithActionAttribute)
        trees.push({ tree, title: `Downloaded File ${idx + 1}`, id, type: 'downloaded' })
      }
    })
  }

  return trees
}

export function countNodesFromTrees(treesData: FlattenTree[] | null) {
  if (!treesData) {
    return { mappedCounts: null, mappedCountsThreat: null, mostNodesTree: { id: '', type: '' } }
  }

  const mappedCounts: { [key: string]: EmulationCounts } = {}
  const mappedCountsThreat: { [key: string]: EmulationCounts } = {}
  const mostNodesTree = { id: '', type: '' }
  let highestTotal = 0

  for (const data of treesData) {
    const { counts, countsThreat, total } = searchTreeAndCount(data.tree)
    mappedCounts[data.id] = counts
    mappedCountsThreat[data.id] = countsThreat

    if (total > highestTotal) {
      highestTotal = total
      mostNodesTree.id = data.id
      mostNodesTree.type = data.type
    }
  }

  return { mappedCounts, mappedCountsThreat, mostNodesTree }
}

/**
 * Calculate Aether Threat Reputation step info from IOCs
 *
 * @param updatedIocs - IOCs with updated verdict information from context
 * @returns ThreatReputationStepInfo with highest verdict and count
 */
export function calculateThreatReputationInfo(updatedIocs: IOCGroupedByFileType | undefined): ThreatReputationStepInfo {
  if (!updatedIocs) {
    return {
      verdictIocsCount: 0,
      totalIocsCount: 0,
      verdict: SignalVerdict.Unknown,
    }
  }

  const iocsByVerdict: VerdictCount = {}
  const { mainFile, ...extractedFiles } = updatedIocs

  if (mainFile) {
    countIOCsByVerdict(mainFile, iocsByVerdict)
  }

  for (const fileCategory in extractedFiles) {
    const filesInCategory = (extractedFiles as any)[fileCategory]
    for (const fileHash in filesInCategory) {
      countIOCsByVerdict(filesInCategory[fileHash], iocsByVerdict)
    }
  }

  const totalIocs = Object.values(iocsByVerdict).reduce((sum, count) => sum + count, 0)
  const riskyIocs = getThreatVerdicts().reduce((sum, verdict) => sum + (iocsByVerdict[verdict] ?? 0), 0)

  // No IOCs to check means undetermined, not benign
  let verdict: SignalVerdict = totalIocs > 0 ? SignalVerdict.Benign : SignalVerdict.Unknown
  let iocsCount = totalIocs

  if (riskyIocs > 0) {
    const threatVerdictOrder = getOrderedThreatVerdicts()
    const highestThreatVerdict = threatVerdictOrder.find((v) => (iocsByVerdict[v] ?? 0) > 0)

    if (highestThreatVerdict) {
      verdict = highestThreatVerdict
      iocsCount = iocsByVerdict[highestThreatVerdict]!
    }
  }

  return {
    verdictIocsCount: iocsCount,
    totalIocsCount: totalIocs,
    verdict,
  }
}

/**
 * Count IOCs by verdict and add to accumulator
 *
 * @param iocTypeGroups - IOCs grouped by type
 * @param accumulator - Verdict count accumulator
 */
function countIOCsByVerdict(iocTypeGroups: { [key: string]: any }, accumulator: VerdictCount): void {
  for (const iocType in iocTypeGroups) {
    const originGroups = iocTypeGroups[iocType]

    if (!Array.isArray(originGroups)) {
      continue
    }

    for (const originGroup of originGroups) {
      if (!originGroup.references || !Array.isArray(originGroup.references)) {
        continue
      }

      for (const ioc of originGroup.references) {
        const verdict: SignalVerdict = (ioc.verdict as SignalVerdict) ?? SignalVerdict.Unknown
        accumulator[verdict] = (accumulator[verdict] || 0) + 1
      }
    }
  }
}

/**
 * Check if all scan subtasks (except similarity search) have finished.
 * Returns false when subtasks haven't been populated yet.
 */
export function checkNonSimilarityStepsDone(progress: FileProgress | undefined): boolean {
  if (!progress) {
    return false
  }
  const mainDone = progress.main?.state === ScanState.Success || progress.main?.state === ScanState.SuccessPartial
  if (!mainDone) {
    return false
  }
  const subtasks: TaskProgress[] = progress.subtasks || []
  if (subtasks.length === 0) {
    return false
  }
  return subtasks
    .filter((t) => !t.name?.toLowerCase().includes('similarity'))
    .every((t) => t.state === ScanState.Success || t.state === ScanState.SuccessPartial)
}

export function getVerdictLevelText(verdict: SignalVerdict, verdictLabel: string, notRiskyLabel: string): string {
  if (verdict === SignalVerdict.Benign) {
    return notRiskyLabel
  }

  if (verdict === SignalVerdict.Malicious) {
    return verdictLabel.split(' ')[0]
  }

  return verdictLabel
}
